/*!
 * Cedarfur Judge.me content override — client-side rewrite of seeded reviews.
 *
 * Judge.me has no public API for editing review text from the theme. This
 * script intercepts the rendered review cards after the widget hydrates and
 * replaces the title + body (and rating, where needed) by matching the
 * author name. Existing photos, dates, and review IDs are preserved.
 *
 * Hooks into `#judgeme_product_reviews` via MutationObserver so it survives
 * pagination and sort-order changes. Idempotent — each card is marked
 * `data-cf-overridden="1"` once processed and skipped on re-run.
 *
 * 2026-05-28
 */
(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // Replacement table — key by normalized author name (lowercase, trimmed)
  // --------------------------------------------------------------------------
  var REPLACEMENTS = {
    'francisco kohler': {
      title: 'No ticks after a full summer of trails',
      body: "We hike almost every weekend in the Pisgah National Forest — Fletcher is a golden mix and a tick magnet. Last summer I was pulling 3-4 a week off him. This season I clipped the Cedarfur pendant in green right to his existing collar. Six weeks in. Two hikes per weekend. We still check him every time we get back to the car, but the count has gone from constant to basically nothing. The peppermint smell holds up longer than I expected — still noticeable after a month. Not magic, just noticeably working. Reordering before the weather gets hot again.",
      rating: 5
    },
    'melodi bruen': {
      title: 'Works quietly in the background',
      body: "I'm a vet tech and I was honestly curious more than convinced when I ordered this. The peppermint and citronella in a silicone diffusion matrix is a real delivery mechanism — it's not the same as a spray that wears off in an hour. Our yard backs up to a wooded lot and our shepherd Nova used to bring ticks in pretty regularly. Since the pendant it's been noticeably less. I still recommend checking your dog after any outdoor time — that doesn't change — but as a supplemental layer this is doing its job. Clean design too.",
      rating: 5
    },
    'phylicia schinner': {
      title: "Finally something that doesn't irritate her skin",
      body: "My beagle Olive has always reacted to topical treatments. Her vet said to try alternatives, so I started looking. Found Cedarfur, was skeptical but ordered anyway. She's worn it for about a month now — no hives, no scratching at the drop site, no staring at me with that sad face. Helps repel from what I can tell; we've found one tick total in four weeks compared to our usual count. She doesn't mind the pendant at all. This is now permanent.",
      rating: 5
    },
    'mary swaniawski': {
      title: "Our puppy doesn't even notice it's there",
      body: "Chester is 14 weeks old and I was nervous about putting anything chemical near him. The Cedarfur pendant clips onto his collar like a tag — he has no idea it's there. We're in the Pacific Northwest where ticks are a real issue spring through fall. No ticks since we started using it. He's young so I'll keep checking him religiously regardless, but at least I'm not slathering pesticides on a puppy.",
      rating: 5
    },
    'xavier champlin': {
      title: 'Skeptic turned regular customer',
      body: "I'll be honest — I ordered this fully expecting it to be garbage. My German shorthair Pepper spends all day running the desert brush. Ticks aren't as bad here as in the South but they're around. I went through the motions, clipped on the green pendant, waited. Three weeks later I found zero on her after a long trail run. Four weeks, same result. The scent diffusion through the silicone tag is the part that surprised me — I thought it would fade in a week but you can still smell the citronella easily at week four. Reordered the 2-pack. Wouldn't have believed it if I hadn't run the experiment myself.",
      rating: 5
    },
    'annice lehner': {
      title: 'Good — wish it came in more sizes',
      body: "Bella is a small rescue terrier mix and the pendant is slightly larger than I expected on her little collar, but it clips on fine and doesn't bother her. What matters is it's working — we're a month in and I've found one tick compared to the 5-6 I was finding before. Peppermint smell is still noticeable after 4 weeks. Gave 4 stars just because a smaller size option for small dogs would be ideal. Otherwise, buying again.",
      rating: 4
    },
    "mack o'reilly": {
      title: 'Cabin weekend — zero ticks on the dog',
      body: "We took Hank (7-year-old rescue mutt) to a cabin in the Smokies for four days. I'd had the Cedarfur pendant on him for about two weeks before the trip. We checked him every morning and evening. Not one tick the whole weekend. Surrounding area was full of tall grass and brush. His buddy who came with us — no pendant — his owners pulled off three. Enough said.",
      rating: 5
    },
    'cinda jacobs': {
      title: 'Three dogs, three pendants — this works',
      body: "I have three labs. Running individual flea-and-tick treatments every month got expensive fast, and one of them, Rosie, always scratched herself raw for a couple days after the chemical drops. I ordered a 3-pack and clipped one on each of them. Rosie's had zero reaction — she doesn't even notice it's there. We're in a rural area and the dogs are in tall grass daily. Way fewer ticks than before. Still checking regularly, but the difference is real. The silicone matrix holding the scent is what I was skeptical about at first — turns out it's not a gimmick. Genuinely holding up.",
      rating: 5
    },
    'hayden becker': {
      title: 'Senior dog, chemical-sensitive — this is our solution',
      body: "Grizzly is 12 and his system doesn't handle topical treatments well anymore — upsets his stomach if he licks near the drop site, and he always does. The vet cleared us to try non-chemical options. Cedarfur pendant, black color, clipped it right onto the collar he already wears. No reaction. He's a yard dog, spends hours outside in the summer. We've found way less ticks than previous years. At 12, I want the least invasive, lowest-stress option I can find. This fits.",
      rating: 5
    },
    'jerlene koss': {
      title: 'Promising but need longer to judge',
      body: "Two weeks in with our lab Remy. Hard to say definitively yet — we've had two rainy weeks so she's been in the yard less than usual. Found one tick on her, which is better than normal but I can't credit the pendant fully with such limited data. The pendant itself is quality — solid clip, not flimsy, holds up to a wet dog. The green color is nice, matches her collar. I'll come back and update once we've had her out on real hikes. Not a bad product, just being honest that two weeks isn't enough.",
      rating: 3
    }
  };

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------
  function normalize(s) {
    return (s || '')
      .replace(/’/g, "'") // curly apostrophe → straight
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function setRating(ratingEl, newScore) {
    if (!ratingEl) return;
    // Judge.me uses .jdgm-star.jdgm-star--on and .jdgm-star--off / --half.
    // Toggle the top-N stars on and the rest off.
    var stars = ratingEl.querySelectorAll('.jdgm-star');
    if (!stars.length) return;
    stars.forEach(function (s, i) {
      s.classList.remove('jdgm-star--on', 'jdgm-star--off', 'jdgm-star--half');
      s.classList.add(i < newScore ? 'jdgm-star--on' : 'jdgm-star--off');
    });
    ratingEl.setAttribute('data-score', String(newScore));
  }

  function setTitle(card, newTitle) {
    var titleEl = card.querySelector('.jdgm-rev__title');
    if (titleEl) {
      titleEl.textContent = newTitle;
      titleEl.style.display = ''; // ensure visible even if Judge.me hid it
      return;
    }
    // No existing title element — inject one before the body
    var bodyEl = card.querySelector('.jdgm-rev__body');
    if (!bodyEl) return;
    var injected = document.createElement('div');
    injected.className = 'jdgm-rev__title';
    injected.textContent = newTitle;
    bodyEl.parentNode.insertBefore(injected, bodyEl);
  }

  function setBody(card, newBody) {
    var bodyEl = card.querySelector('.jdgm-rev__body');
    if (!bodyEl) return;
    // Judge.me may render body with inner <p> or as text — set as a single <p>.
    bodyEl.innerHTML = '';
    var p = document.createElement('p');
    p.textContent = newBody;
    bodyEl.appendChild(p);
    // Clear any "read more" truncation state Judge.me may have applied
    bodyEl.classList.remove('jdgm-rev__body--collapsed');
    var rm = card.querySelector('.jdgm-rev__body-readmore, .jdgm-rev__readmore');
    if (rm) rm.style.display = 'none';
  }

  // --------------------------------------------------------------------------
  // Core
  // --------------------------------------------------------------------------
  function processCard(card) {
    if (!card || card.getAttribute('data-cf-overridden') === '1') return;
    var authorEl = card.querySelector('.jdgm-rev__author');
    if (!authorEl) return;
    var key = normalize(authorEl.textContent);
    var spec = REPLACEMENTS[key];
    if (!spec) return; // unknown author — leave it alone

    setTitle(card, spec.title);
    setBody(card, spec.body);
    var ratingEl = card.querySelector('.jdgm-rev__rating');
    if (ratingEl) setRating(ratingEl, spec.rating);

    card.setAttribute('data-cf-overridden', '1');
  }

  function processAll(root) {
    var cards = (root || document).querySelectorAll('.jdgm-rev');
    cards.forEach(processCard);
  }

  function start() {
    var widget = document.getElementById('judgeme_product_reviews');
    if (!widget) {
      // Widget not on this page — bail
      return;
    }

    // Initial pass — handles SSR / prerendered cards
    processAll(widget);

    // Observe re-renders (pagination, sort change, ajax replace)
    var obs = new MutationObserver(function (mutations) {
      // Debounce: skip the observer firing on our own setAttribute below by
      // only acting when a node has actually been added.
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          processAll(widget);
          return;
        }
      }
    });
    obs.observe(widget, { childList: true, subtree: true });

    // Belt-and-suspenders: Judge.me sometimes hydrates after multiple async
    // ticks. Retry the initial scan a few times in case the first pass ran
    // before any review cards existed.
    var tries = 0;
    var retry = setInterval(function () {
      tries++;
      if (widget.querySelector('.jdgm-rev[data-cf-overridden="1"]') || tries > 20) {
        clearInterval(retry);
        return;
      }
      processAll(widget);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
